// Browser-safe minimal EXIF reader for JPEG/TIFF files.
// Extracts camera make/model, capture timestamp, software, and GPS coordinates.

export type ExifData = {
  make?: string;
  model?: string;
  software?: string;
  dateTime?: string;
  lensModel?: string;
  orientation?: number;
  gps?: { lat: number; lon: number; altitude?: number };
  raw: Record<string, string | number>;
};

const TAGS: Record<number, string> = {
  0x010f: "Make",
  0x0110: "Model",
  0x0112: "Orientation",
  0x0131: "Software",
  0x0132: "DateTime",
  0x9003: "DateTimeOriginal",
  0x9004: "DateTimeDigitized",
  0xa434: "LensModel",
  0x013b: "Artist",
  0x8298: "Copyright",
  0x829a: "ExposureTime",
  0x829d: "FNumber",
  0x8827: "ISOSpeedRatings",
  0x920a: "FocalLength",
};

const GPS_TAGS: Record<number, string> = {
  0x0001: "GPSLatitudeRef",
  0x0002: "GPSLatitude",
  0x0003: "GPSLongitudeRef",
  0x0004: "GPSLongitude",
  0x0005: "GPSAltitudeRef",
  0x0006: "GPSAltitude",
  0x0007: "GPSTimeStamp",
  0x001d: "GPSDateStamp",
};

function readAscii(view: DataView, offset: number, length: number) {
  let s = "";
  for (let i = 0; i < length; i++) {
    const c = view.getUint8(offset + i);
    if (c === 0) break;
    s += String.fromCharCode(c);
  }
  return s.trim();
}

function readValue(view: DataView, entry: number, tiff: number, le: boolean): string | number | number[] | undefined {
  const type = view.getUint16(entry + 2, le);
  const count = view.getUint32(entry + 4, le);
  const sizes: Record<number, number> = { 1: 1, 2: 1, 3: 2, 4: 4, 5: 8, 7: 1, 9: 4, 10: 8 };
  const size = sizes[type];
  if (!size) return undefined;
  const total = size * count;
  const valueOffset = total <= 4 ? entry + 8 : tiff + view.getUint32(entry + 8, le);
  if (valueOffset < 0 || valueOffset + total > view.byteLength) return undefined;
  if (type === 2) return readAscii(view, valueOffset, count);
  const out: number[] = [];
  for (let i = 0; i < count; i++) {
    const o = valueOffset + i * size;
    if (type === 1 || type === 7) out.push(view.getUint8(o));
    else if (type === 3) out.push(view.getUint16(o, le));
    else if (type === 4) out.push(view.getUint32(o, le));
    else if (type === 9) out.push(view.getInt32(o, le));
    else if (type === 5) out.push(view.getUint32(o, le) / (view.getUint32(o + 4, le) || 1));
    else if (type === 10) out.push(view.getInt32(o, le) / (view.getInt32(o + 4, le) || 1));
  }
  return count === 1 ? out[0] : out;
}

function dms(parts: number[] | undefined, ref: string | undefined): number | undefined {
  if (!Array.isArray(parts) || parts.length < 3) return undefined;
  const [d, m, s] = parts;
  let v = d + m / 60 + s / 3600;
  if (ref === "S" || ref === "W") v = -v;
  return Number.isFinite(v) ? Number(v.toFixed(6)) : undefined;
}

export function parseExif(buffer: ArrayBuffer): ExifData | null {
  try {
    const view = new DataView(buffer);
    if (view.byteLength < 12) return null;
    let tiff = -1;
    if (view.getUint16(0, false) === 0xffd8) {
      // JPEG: walk markers for APP1/Exif
      let offset = 2;
      while (offset + 4 < view.byteLength) {
        if (view.getUint8(offset) !== 0xff) break;
        const marker = view.getUint8(offset + 1);
        const len = view.getUint16(offset + 2, false);
        if (marker === 0xe1 && readAscii(view, offset + 4, 4) === "Exif") {
          tiff = offset + 10;
          break;
        }
        if (marker === 0xda) break;
        offset += 2 + len;
      }
    } else {
      const bom = view.getUint16(0, false);
      if (bom === 0x4949 || bom === 0x4d4d) tiff = 0;
    }
    if (tiff < 0 || tiff + 8 > view.byteLength) return null;

    const le = view.getUint16(tiff, false) === 0x4949;
    const ifd0 = tiff + view.getUint32(tiff + 4, le);
    const raw: Record<string, string | number> = {};
    let exifIfd = -1;
    let gpsIfd = -1;

    const walk = (dir: number, tagMap: Record<number, string>, gps = false) => {
      if (dir <= 0 || dir + 2 > view.byteLength) return;
      const count = view.getUint16(dir, le);
      for (let i = 0; i < count; i++) {
        const entry = dir + 2 + i * 12;
        if (entry + 12 > view.byteLength) break;
        const tag = view.getUint16(entry, le);
        if (!gps && tag === 0x8769) { exifIfd = tiff + (view.getUint32(entry + 8, le) || 0); continue; }
        if (!gps && tag === 0x8825) { gpsIfd = tiff + (view.getUint32(entry + 8, le) || 0); continue; }
        const name = tagMap[tag];
        if (!name) continue;
        const value = readValue(view, entry, tiff, le);
        if (value === undefined) continue;
        raw[name] = Array.isArray(value) ? value.join(", ") : value;
        if (gps) gpsRaw[name] = value as never;
      }
    };
    const gpsRaw: Record<string, string | number | number[]> = {};

    walk(ifd0, TAGS);
    if (exifIfd > 0) walk(exifIfd, TAGS);
    if (gpsIfd > 0) walk(gpsIfd, GPS_TAGS, true);

    const lat = dms(gpsRaw.GPSLatitude as number[], gpsRaw.GPSLatitudeRef as string);
    const lon = dms(gpsRaw.GPSLongitude as number[], gpsRaw.GPSLongitudeRef as string);

    const data: ExifData = {
      make: raw.Make as string | undefined,
      model: raw.Model as string | undefined,
      software: raw.Software as string | undefined,
      dateTime: (raw.DateTimeOriginal ?? raw.DateTime) as string | undefined,
      lensModel: raw.LensModel as string | undefined,
      orientation: raw.Orientation as number | undefined,
      gps: lat !== undefined && lon !== undefined
        ? { lat, lon, altitude: typeof gpsRaw.GPSAltitude === "number" ? gpsRaw.GPSAltitude : undefined }
        : undefined,
      raw,
    };
    return Object.keys(raw).length || data.gps ? data : null;
  } catch {
    return null;
  }
}